import { Button, Stack, Typography } from '@mui/material'
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { generatePath } from 'react-router-dom'
import { InformationModal } from 'utoniq-ui'

import { useTranslation } from '../i18n'
import { useSettingUserInfoModal } from '../membership/useSettingUserInfoModal'
import { useRouter } from '../router'
import { useMyFavoriteViewModel } from './viewModel'

type Options = {
  requiredFavoriteMemberArtistId?: string
}

type SettingMyFavoriteModalContext = {
  isOpen: boolean
  showModal: (options: Options) => void
}

const Context = createContext({} as SettingMyFavoriteModalContext)

export function useSettingMyFavoriteModal() {
  return useContext(Context)
}

type SettingMyFavoriteModalProviderProps = PropsWithChildren

export const SettingMyFavoriteModalProvider: FC<SettingMyFavoriteModalProviderProps> =
  ({ children }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [showOptions, setShowOptions] = useState<Options>({})
    const { state } = useMyFavoriteViewModel()
    const { routerPages, router } = useRouter()
    const { isOpen: isSettingUserInfoModalOpen, isSuccessModalOpen } =
      useSettingUserInfoModal()

    // 推しメンバー設定要否に基づいてモーダルの初期表示状態を設定
    useEffect(() => {
      if (
        state.isRequiredFavoriteMember &&
        !isSettingUserInfoModalOpen &&
        !isSuccessModalOpen &&
        router.pathname !== routerPages.MyProfile &&
        router.pathname !== routerPages.FavoriteMembersSelect
      ) {
        setShowOptions({ requiredFavoriteMemberArtistId: state.artistId })
        setOpen(true)
      }
    }, [
      state.isRequiredFavoriteMember,
      isSettingUserInfoModalOpen,
      isSuccessModalOpen,
    ])

    const showModal = useCallback((options: Options) => {
      setShowOptions(options)
      setOpen(true)
    }, [])

    return (
      <Context.Provider value={{ showModal, isOpen: open }}>
        {children}
        <SettingMyFavoriteModal
          open={open}
          onClose={() => {
            setOpen(false)
            router.push(
              generatePath(routerPages.FavoriteMembersSelect, {
                artistId: showOptions.requiredFavoriteMemberArtistId,
              })
            )
            // 都度artistIdをセットするためにリセット
            setShowOptions({})
          }}
        />
      </Context.Provider>
    )
  }

const SettingMyFavoriteModal: FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useTranslation('settingMyFavotite')
  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={
        <Typography variant="h5" sx={{ pb: 3 }}>
          {t('title', '推しメンバーが登録されていません')}
        </Typography>
      }
      content={
        <Typography variant="body2" sx={{ px: 5, pb: 7 }}>
          {t(
            'description',
            'メンバーシップ限定コンテンツをお楽しみいただくために必要な項目を入力してください。'
          )}
        </Typography>
      }
      buttons={
        <Stack spacing={1}>
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={() => onClose()}
          >
            {t('register', '登録する')}
          </Button>
        </Stack>
      }
      sx={{
        minWidth: 300,
      }}
    />
  )
}
